/**
 * Payroll Current Run tab — split out of pages/Payroll.jsx (2026-02).
 *
 * Contains CurrentRun (the tab body) plus every dialog/subcomponent
 * it owns: PartialAttendanceDialog, UnmatchedWarning, AttributePill,
 * ValidationBlock, Vitem, DetailTable, PayrollSummarySection, KpiRow,
 * UnlockDialog, GustoVerifyDialog. Zero behaviour change from the
 * pre-split inline definitions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { fmtDate, fmtMonYY } from '../../lib/date';
import { useAuth } from '../../lib/AuthContext';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { TherapistForm } from './TherapistSetup';
import {
  Calculator, CheckCircle2, AlertTriangle, Lock, Unlock, Printer,
  BarChart3, Users, Settings, Plus, Shield, ArrowLeft, RefreshCw, UserPlus, Search,
  Download, Upload, DollarSign, Pause, X, Copy, RotateCcw,
} from 'lucide-react';
import { UndoPayrollDialog } from './UndoPayrollDialog';
import {
  C, usd, usdShort, pct, int,
  CATEGORY_GROUP, CATEGORY_GROUP_ORDER, _lastName,
  sortAndGroupRows,
  _ctDate, _isoLocal, biweeklyDefaults,
} from '../../lib/payrollHelpers';

// Format a payable-units value as an integer; retained as a wrapper
// around the shared `int()` so a single hook exists if we ever
// introduce fractional display again.
const fmtUnits = (n) => int(n);

function PartialAttendanceDialog({ message, coverage, onProceed, onCancel }) {
  const missing = coverage?.missing_dates || [];
  const covered = coverage?.days_covered || 0;
  const expected = coverage?.days_expected || 0;
  return (
    <Dialog open onOpenChange={o => !o && onCancel()}>
      <DialogContent data-testid="pr-partial-attendance-dialog">
        <DialogHeader>
          <DialogTitle>Attendance report is incomplete</DialogTitle>
          <DialogDescription>
            Only {covered} of {expected} days in this pay period have Client Attendance rows. Re-uploading the report is the safest fix; you can also continue with what's loaded.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm mb-3">{message}</div>
        {missing.length > 0 && (
          <div className="rounded-md border p-3 bg-amber-50 max-h-40 overflow-auto text-xs font-mono" data-testid="pr-partial-missing-dates">
            <div className="font-semibold mb-1 not-italic">Missing dates ({missing.length}):</div>
            {missing.join(', ')}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="pr-partial-cancel">Cancel — I'll re-upload</Button>
          <Button onClick={onProceed} data-testid="pr-partial-proceed">Proceed with the days I have</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════ 1. Current Run ═══════════════
function CurrentRun() {
  const { user } = useAuth();
  const [defaults] = useState(biweeklyDefaults);
  const [periodStart, setPeriodStart] = useState(defaults.period_start);
  const [periodEnd, setPeriodEnd]     = useState(defaults.period_end);
  const [coverage, setCoverage] = useState(null);
  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [unlockRow, setUnlockRow] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editingTherapist, setEditingTherapist] = useState(null);
  // Gusto verification modal: single-row or a queue for bulk mark-processed.
  //   queue    → array of row objects waiting for Gusto entry
  //   index    → current position in queue (0-based)
  //   modalRow → the row currently being verified (derived), null when closed
  const [gustoQueue, setGustoQueue] = useState([]);
  const [gustoIndex, setGustoIndex] = useState(0);
  const gustoActive = gustoQueue.length > 0 && gustoIndex < gustoQueue.length;
  const gustoRow = gustoActive ? gustoQueue[gustoIndex] : null;

  // Overpayment detail dialog — opens when HR clicks the red chip on a row.
  // Carries the therapist row so we can show Client + DOS + status flip and
  // let HR Hold/Waive each adjustment or auto-apply the whole batch.
  const [overpaymentRow, setOverpaymentRow] = useState(null);
  const [undoOpen, setUndoOpen] = useState(false);

  // ── Check coverage as HR types the period ─────────
  const checkCoverage = useCallback(async () => {
    try {
      const { data } = await api.get('/payroll/coverage', {
        params: { period_start: periodStart, period_end: periodEnd },
      });
      setCoverage(data);
    } catch { setCoverage(null); }
  }, [periodStart, periodEnd]);
  useEffect(() => { checkCoverage(); }, [checkCoverage]);

  // ── Attempt to load the most recent draft for this window ──
  useEffect(() => {
    api.get('/payroll/runs', { params: { limit: 5 } }).then(({ data }) => {
      const draft = (data.runs || []).find(r => r.status === 'draft'
        && r.period_start === periodStart && r.period_end === periodEnd);
      if (draft) loadRun(draft.run_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRun = async (rid) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payroll/runs/${rid}`);
      setRun(data.run); setRows(data.rows); setSummary(data.summary);
    } catch { toast.error('Could not load payroll run'); }
    finally { setLoading(false); }
  };

  // ── Partial-attendance confirm modal (iter 212) ─────────────
  // Backend 409 with code='partial_attendance' surfaces which days in
  // the requested period are missing attendance rows. HR confirms
  // before we re-POST with force:true.
  const [partialAttPrompt, setPartialAttPrompt] = useState(null);
  const proceedWithPartial = async () => {
    const req = partialAttPrompt?.request;
    setPartialAttPrompt(null);
    if (!req) return;
    setLoading(true);
    try {
      const { data } = await req();
      setRun(data.run); setRows(data.rows); setSummary(data.summary);
      toast.success('Payroll run started with the days available');
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Could not create run'); }
    finally { setLoading(false); }
  };

  const createRun = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/payroll/runs', {
        period_start: periodStart, period_end: periodEnd,
      });
      setRun(data.run); setRows(data.rows); setSummary(data.summary);
      toast.success('Payroll run started');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'missing_data') {
        toast.error(detail.message || 'Missing data — upload Attendance / Sessions.');
      } else if (detail?.code === 'partial_attendance') {
        // Show the confirm modal with the missing dates so HR decides.
        setPartialAttPrompt({
          message: detail.message,
          coverage: detail.coverage,
          request: () => api.post('/payroll/runs', {
            period_start: periodStart, period_end: periodEnd, force: true,
          }),
        });
      } else {
        toast.error('Could not create run');
      }
    } finally { setLoading(false); }
  };

  const recalc = async () => {
    if (!run) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/payroll/runs/${run.run_id}/recalculate`);
      setRun(data.run); setRows(data.rows); setSummary(data.summary);
      toast.success('Recalculated from latest data');
    } catch { toast.error('Recalc failed'); }
    finally { setLoading(false); }
  };

  // Iter 192 — change the run's date range in-place while it's still a
  // draft. Backend recomputes rows against the new window and preserves
  // any HR adjustments + processed flags already keyed. Overlap with a
  // finalized run is not blocked here — the calculation runs; only the
  // finalize/process actions get gated further down.
  const updatePeriod = async (nextStart, nextEnd) => {
    if (!run) return;
    if (!nextStart || !nextEnd) { toast.error('Pick both start and end dates.'); return; }
    if (nextStart > nextEnd)   { toast.error('Start date must be on or before end date.'); return; }
    const doPatch = (force = false) => api.patch(`/payroll/runs/${run.run_id}/period`, {
      period_start: nextStart, period_end: nextEnd, ...(force ? { force: true } : {}),
    });
    setLoading(true);
    try {
      const { data } = await doPatch(false);
      setRun(data.run); setRows(data.rows); setSummary(data.summary);
      const overlaps = (data.run.overlap_finalized_runs || []).length;
      toast.success(overlaps
        ? `Period updated — ⚠ overlaps ${overlaps} finalized run${overlaps === 1 ? '' : 's'}, finalize will be blocked.`
        : 'Period updated — recomputed against new window.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'missing_data') {
        toast.error(detail.message || 'Missing data for that window — upload Attendance / Sessions first.');
      } else if (detail?.code === 'partial_attendance') {
        setPartialAttPrompt({
          message: detail.message,
          coverage: detail.coverage,
          request: async () => {
            const { data } = await doPatch(true);
            return { data };
          },
        });
      } else {
        toast.error(detail?.message || 'Could not update period');
      }
    } finally { setLoading(false); }
  };

  const setAdjustment = async (therapistId, value) => {
    if (!run) return;
    try {
      const { data } = await api.post(
        `/payroll/runs/${run.run_id}/rows/${therapistId}/adjust`,
        { session_adjustment: Number(value || 0) },
      );
      setRows(prev => prev.map(r => r.therapist_id === therapistId
        ? { ...r, session_adjustment: data.session_adjustment, payable_units: data.payable_units }
        : r));
    } catch (e) {
      if (e?.response?.status === 423) toast.error('Row is processed — unlock first.');
      else toast.error('Adjustment failed');
    }
  };

  const processRow = (row) => {
    // Open the Gusto Verification modal for this single row.
    setGustoQueue([row]);
    setGustoIndex(0);
  };

  // Called by GustoVerifyDialog with the entries payload (already scoped to
  // the therapist's category by the dialog) and an optional override reason.
  const submitGustoRow = async (row, entries, overrideReason) => {
    const payload = {};
    // The dialog passes only the fields relevant to this therapist's
    // category — everyone gets payable_units / lc_ns_dollars / bonus_dollars;
    // Contractor-CPT rows additionally send the 3 CPT counts +
    // cpt_pay_dollars. Coerce numerics; leave undefined for unset keys so
    // the backend treats them as ignored.
    Object.entries(entries).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) return;
      payload[k] = Number(v);
    });
    if (overrideReason) payload.override_reason = overrideReason;
    const { data } = await api.post(
      `/payroll/runs/${run.run_id}/rows/${row.therapist_id}/process`,
      payload,
    );
    setRows(prev => prev.map(r => r.therapist_id === row.therapist_id
      ? { ...r, processed: true, processed_by: user.email, processed_at: new Date().toISOString(),
          gusto_verified: !!data?.gusto_verified, gusto_override: !!data?.gusto_override }
      : r));
    return data;
  };

  const advanceGustoQueue = () => {
    // If more rows queued, step forward. Otherwise close.
    if (gustoIndex + 1 < gustoQueue.length) {
      setGustoIndex(i => i + 1);
    } else {
      setGustoQueue([]); setGustoIndex(0);
    }
  };
  const skipGustoRow = () => {
    // Skip this therapist without processing them and move to next in queue.
    advanceGustoQueue();
  };
  const closeGustoModal = () => {
    // Cancel: wipe the queue entirely.
    setGustoQueue([]); setGustoIndex(0);
  };

  const submitUnlock = async (reason) => {
    if (!unlockRow) return;
    try {
      await api.post(
        `/payroll/runs/${run.run_id}/rows/${unlockRow.therapist_id}/unlock`,
        { reason },
      );
      setRows(prev => prev.map(r => r.therapist_id === unlockRow.therapist_id
        ? { ...r, processed: false, processed_by: null, processed_at: null }
        : r));
      setUnlockRow(null);
    } catch { toast.error('Unlock failed'); }
  };

  const toggleRowSelected = (tid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  };
  const toggleAllSelected = (ids, on) => {
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => { if (on) next.add(id); else next.delete(id); });
      return next;
    });
  };
  const bulkMarkProcessed = () => {
    if (!run || selected.size === 0) return;
    // Open the Gusto Verification modal as a per-row wizard. Only include
    // rows that are actually still unprocessed.
    const ids = Array.from(selected);
    const queue = rows.filter(r => ids.includes(r.therapist_id) && !r.processed);
    if (queue.length === 0) { toast.info('Selected rows are already processed'); return; }
    // Preserve the same group/last-name ordering so HR walks the queue in
    // the same order they see on-screen.
    setGustoQueue(sortAndGroupRows(queue));
    setGustoIndex(0);
    // Keep the selection until the queue finishes so the user has a
    // reference. We'll clear it in the wizard's onFinish.
  };
  // Called when the last queued row is processed successfully.
  const onGustoQueueDone = (processedCount) => {
    if (gustoQueue.length > 1) {
      toast.success(`Marked ${processedCount} row(s) processed`);
      setSelected(new Set());
    }
  };

  // Fetch the therapist doc by id and open the inline setup dialog. Used
  // when HR clicks a therapist's name in the detail table to quickly
  // review/tweak their config (rates, type, bonus threshold) without
  // navigating away from the payroll run.
  const openTherapistEditor = async (therapistId) => {
    try {
      const { data } = await api.get('/payroll/therapists', { params: { include_inactive: true } });
      const t = (data.therapists || []).find(x => x.therapist_id === therapistId);
      if (!t) { toast.error('Therapist config not found'); return; }
      setEditingTherapist(t);
    } catch { toast.error('Could not open therapist setup'); }
  };
  const saveTherapistEdit = async (payload) => {
    if (!editingTherapist) return;
    try {
      await api.put(`/payroll/therapists/${editingTherapist.therapist_id}`, payload);
      toast.success('Therapist updated');
      setEditingTherapist(null);
      // Rate/type changes affect subsequent calcs; recalc to refresh the row.
      if (run && run.status !== 'finalized') recalc();
    } catch { toast.error('Save failed'); }
  };

  const saveSummary = async (values) => {
    setSavingSummary(true);
    try {
      const { data } = await api.put(`/payroll/runs/${run.run_id}/summary`, values);
      setSummary(data);
      setRun(r => ({ ...r, undo_eligible: true }));
      toast.success('Summary saved');
    } catch { toast.error('Summary save failed'); }
    finally { setSavingSummary(false); }
  };

  const finalize = async () => {
    try {
      await api.post(`/payroll/runs/${run.run_id}/finalize`);
      toast.success('Payroll period finalized');
      loadRun(run.run_id);
    } catch (e) {
      // Iter 221 — surface the ACTUAL blocking reason (unprocessed rows,
      // Gusto-not-verified, overlap, unrecoverable data, etc.) so HR knows
      // exactly which therapist rows to fix instead of chasing a generic
      // "Finalize failed" toast.
      const detail = e?.response?.data?.detail;
      const names = (detail && detail.outstanding) || [];
      const preview = names.slice(0, 5).join(', ');
      const suffix = names.length > 5 ? `, +${names.length - 5} more` : '';
      const tail = names.length > 0 ? `: ${preview}${suffix}` : '';
      let msg;
      if (detail?.code === 'unprocessed_rows') {
        msg = `Finalize blocked — ${names.length} unprocessed row(s)${tail}`;
      } else if (detail?.code === 'gusto_not_verified') {
        msg = `Finalize blocked — ${names.length} row(s) missing Gusto verification${tail}`;
      } else if (typeof detail === 'string') {
        msg = `Finalize blocked — ${detail}`;
      } else if (detail?.message) {
        msg = `Finalize blocked — ${detail.message}${tail}`;
      } else {
        msg = e?.message ? `Finalize failed — ${e.message}` : 'Finalize failed';
      }
      toast.error(msg, { duration: 10000 });
    }
  };

  // ── UI ────────────────────────────────────────────
  if (!run) {
    return (
      <>
      <Card className="p-5 border" style={{ borderColor: C.border }} data-testid="pr-run-setup">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.gold }}>
          <Calculator size={12} /> START A NEW PAYROLL RUN
        </div>
        <div className="mt-3 grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider" style={{ color: C.soft }}>Pay period start (Thursday)</label>
            <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} data-testid="pr-period-start" className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider" style={{ color: C.soft }}>Pay period end (Wednesday)</label>
            <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} data-testid="pr-period-end" className="mt-1" />
          </div>
          <div className="flex items-end">
            <Button
              onClick={createRun}
              disabled={loading || !coverage || coverage.missing?.attendance || coverage.missing?.sessions}
              className="h-10 bg-[#4A3B5F] hover:bg-[#2D2833] text-white w-full"
              data-testid="pr-create-run"
            >
              {loading ? 'Starting…' : 'Start payroll run'}
            </Button>
          </div>
        </div>
        {coverage && (() => {
          const missingData = coverage.missing?.attendance || coverage.missing?.sessions;
          const partial = coverage.incomplete_attendance && !missingData;
          const bg = missingData ? '#FDECEA' : partial ? '#FDF5E7' : '#E8F3EE';
          const border = missingData ? '#F1BAB4' : partial ? '#E8CE9C' : '#B8D6C7';
          const iconColor = missingData ? C.rose : partial ? '#B08A3A' : C.ok;
          return (
            <div className="mt-4 rounded-xl border p-3" style={{ background: bg, borderColor: border }} data-testid="pr-coverage">
              <div className="flex items-start gap-2 text-[13px]" style={{ color: C.ink }}>
                {missingData ? (
                  <><AlertTriangle size={14} style={{ color: iconColor, marginTop: 2 }} />
                    <div>
                      <b>Data missing for this window.</b><br />
                      {coverage.missing.attendance && <>• Client Attendance report — 0 rows in {periodStart} → {periodEnd}. Upload via Data Import.<br /></>}
                      {coverage.missing.sessions   && <>• Appointment Status report — 0 rows in {periodStart} → {periodEnd}. Upload via Data Import.<br /></>}
                    </div>
                  </>
                ) : partial ? (
                  <><AlertTriangle size={14} style={{ color: iconColor, marginTop: 2 }} />
                    <div data-testid="pr-coverage-partial">
                      <b>Attendance is incomplete — {coverage.days_covered}/{coverage.days_expected} days loaded.</b><br />
                      Missing: {(coverage.missing_dates || []).slice(0, 6).join(', ')}
                      {(coverage.missing_dates || []).length > 6 && ` (+${coverage.missing_dates.length - 6} more)`}. You can re-upload the Client Attendance report, or click Start payroll run and confirm to proceed with the days you have.
                    </div>
                  </>
                ) : (
                  <><CheckCircle2 size={14} style={{ color: iconColor, marginTop: 2 }} />
                    <div>
                      Ready to run — {int(coverage.attendance_rows)} attendance rows, {int(coverage.sessions_rows)} session rows in window.
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </Card>
      {partialAttPrompt && (
        <PartialAttendanceDialog
          message={partialAttPrompt.message}
          coverage={partialAttPrompt.coverage}
          onProceed={proceedWithPartial}
          onCancel={() => setPartialAttPrompt(null)}
        />
      )}
      </>
    );
  }

  const totalRows = rows.length;
  const processedCount = rows.filter(r => r.processed).length;
  const outstanding = rows.filter(r => !r.processed).map(r => r.therapist_name_snapshot);
  const grand = rows.reduce((acc, r) => ({
    show: acc.show + (r.show_units || 0),
    lc: acc.lc + (r.lc_units || 0),
    ns: acc.ns + (r.ns_units || 0),
    canceled: acc.canceled + (r.canceled_units || 0),
    total: acc.total + (r.total_appts || 0),
    adj: acc.adj + (r.session_adjustment || 0),
    payable: acc.payable + (r.payable_units || 0),
    lcns: acc.lcns + (r.lc_ns_comp_cents || 0),
    bonus: acc.bonus + (r.bonus_amount_cents || 0),
    cpt507: acc.cpt507 + (r.cpt_92507_units || 0),
    cptEval: acc.cptEval + ((r.cpt_92523_units || 0) + (r.cpt_92610_units || 0)),
    cpt526: acc.cpt526 + (r.cpt_92526_units || 0),
    therapy: acc.therapy + (r.therapy_comp_cents || 0),
    evalc:   acc.evalc   + (r.eval_comp_cents || 0),
    overpaymentCents: acc.overpaymentCents + (r.overpayment_adjustment_cents || 0),
    overpaymentCount: acc.overpaymentCount + (r.overpayment_adjustment_count || 0),
    overpaymentTherapists: acc.overpaymentTherapists + ((r.overpayment_adjustment_cents || 0) > 0 ? 1 : 0),
  }), { show: 0, lc: 0, ns: 0, canceled: 0, total: 0, adj: 0, payable: 0, lcns: 0, bonus: 0, cpt507: 0, cptEval: 0, cpt526: 0, therapy: 0, evalc: 0, overpaymentCents: 0, overpaymentCount: 0, overpaymentTherapists: 0 });

  return (
    <div className="space-y-4" data-testid="pr-current-run">
      {/* Overpayment banner — top-of-run alert when prior-period paid
          sessions have been changed in SP since finalize. Big enough that
          HR cannot miss it on a draft run. Finalized runs don't show it
          (adjustments only apply to the NEXT draft). Also surfaces
          "orphan" adjustments — flags for therapists who are NOT in this
          draft's rows (e.g. left the practice, PTO this period) so HR
          still sees every open flag. */}
      {run.status === 'draft' && ((grand.overpaymentCents || 0) > 0 || ((run.overpayment_orphans?.total_cents || 0) > 0)) && (
        <OverpaymentBanner
          runId={run.run_id}
          rows={rows}
          cents={grand.overpaymentCents}
          count={grand.overpaymentCount}
          therapists={grand.overpaymentTherapists}
          orphans={run.overpayment_orphans || { orphans: [], total_cents: 0, therapist_count: 0 }}
          onApplyAll={async () => {
            if (!window.confirm(
              `Apply ${grand.overpaymentCount} overpayment adjustment${grand.overpaymentCount === 1 ? '' : 's'} to this draft run?\n\nTotal deduction: ${usd(grand.overpaymentCents)} across ${grand.overpaymentTherapists} therapist${grand.overpaymentTherapists === 1 ? '' : 's'}.\n\nAny adjustment you've marked Hold or Waived will be skipped. Rows already Processed will be skipped — unlock them first if you want the adjustment applied.`
            )) return;
            try {
              const { data } = await api.post(`/payroll/overpayments/auto-apply/${run.run_id}`);
              toast.success(`Applied ${data.applied} row(s) · ${usd(data.cents_total)} deducted`);
              loadRun(run.run_id);
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Auto-apply failed');
            }
          }}
        />
      )}
      {/* Overlap banner (iter 192) — shows any finalized runs whose date
          range intersects this run's. Warns the user, but the calculation
          still runs; only finalize + per-row process actions get gated. */}
      {(run.overlap_finalized_runs || []).length > 0 && run.status !== 'finalized' && (
        <div
          className="rounded-xl border p-4 flex items-start gap-3"
          style={{ borderColor: '#B08A3A', background: '#FDF5E7', color: '#5C4A1A' }}
          data-testid="pr-overlap-banner"
        >
          <AlertTriangle size={18} style={{ color: '#B08A3A', marginTop: 2 }} />
          <div className="text-[13px] leading-snug flex-1">
            <div className="font-semibold mb-0.5">Warning: this date range overlaps a finalized run.</div>
            <div>
              Overlaps with{' '}
              {(run.overlap_finalized_runs || []).map((o, i) => (
                <span key={o.run_id}>
                  <b>{o.period_start} → {o.period_end}</b>
                  {i < run.overlap_finalized_runs.length - 1 ? ', ' : ''}
                </span>
              ))}
              . Calculation will proceed but this run cannot be finalized (Mark-processed + Finalize disabled) until the overlap is resolved — either adjust the dates or unlock the prior run.
            </div>
          </div>
        </div>
      )}

      {/* Sticky processed counter */}
      <div className="sticky top-0 z-20 rounded-xl border shadow-sm px-4 py-3 flex items-center gap-4 backdrop-blur-md"
           style={{ background: 'rgba(251,248,244,0.94)', borderColor: C.border }} data-testid="pr-progress-bar">
        {run.status === 'finalized' ? (
          <div className="text-[13px] font-semibold" style={{ color: C.ink }} data-testid="pr-period-static">
            {run.period_start} → {run.period_end}
          </div>
        ) : (
          <div className="flex items-center gap-1.5" data-testid="pr-period-edit">
            <input
              type="date"
              defaultValue={run.period_start}
              onBlur={e => { const v = e.target.value; if (v && v !== run.period_start) updatePeriod(v, run.period_end); }}
              className="text-[12px] font-semibold rounded border px-2 py-1"
              style={{ color: C.ink, borderColor: C.border }}
              data-testid="pr-period-start-input"
              disabled={loading}
            />
            <span className="text-[13px]" style={{ color: C.ink2 }}>→</span>
            <input
              type="date"
              defaultValue={run.period_end}
              onBlur={e => { const v = e.target.value; if (v && v !== run.period_end) updatePeriod(run.period_start, v); }}
              className="text-[12px] font-semibold rounded border px-2 py-1"
              style={{ color: C.ink, borderColor: C.border }}
              data-testid="pr-period-end-input"
              disabled={loading}
            />
          </div>
        )}
        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#EFE9F5' }}>
          <div className="h-full rounded-full transition-all"
               style={{ width: `${totalRows ? (processedCount / totalRows) * 100 : 0}%`, background: C.ok }} />
        </div>
        <div className="text-[13px] font-semibold tabular-nums" style={{ color: C.ink }} data-testid="pr-progress-counter">
          {processedCount} of {totalRows} processed
        </div>
        <div className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold ${
          run.status === 'finalized' ? 'bg-[#3B6F5D] text-white' : 'bg-[#F1EDF5] text-[#4A3B5F]'
        }`} data-testid="pr-status-pill">
          {run.status}
        </div>
        <Button variant="outline" size="sm" onClick={recalc} disabled={run.status === 'finalized' || loading} data-testid="pr-recalc-btn">
          <RefreshCw size={12} className="mr-1" /> Recalculate
        </Button>
        <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="pr-print-btn">
          <Printer size={12} className="mr-1" /> Print
        </Button>
        {run.status === 'draft' && !run.override_history && (run.undo_eligible || rows.some(r =>
          r.processed || r.session_adjustment || r.overpayment_units_deducted > 0 || (r.unlock_history || []).length)) && (
          <Button variant="outline" size="sm" onClick={() => setUndoOpen(true)} disabled={loading}
                  className="text-red-700 border-red-300 hover:bg-red-50" data-testid="pr-undo-btn">
            <RotateCcw size={12} className="mr-1" /> Undo Payroll
          </Button>
        )}
      </div>
      <UndoPayrollDialog run={run} open={undoOpen} onClose={() => setUndoOpen(false)}
        onUndone={() => { setUndoOpen(false); setRun(null); setRows([]); setSummary(null); setSelected(new Set()); checkCoverage(); }} />

      {/* Floating bulk-processed action bar — pinned to the bottom of
          the viewport so users tagging many clinicians never have to
          scroll back up to reach the button. */}
      {selected.size > 0 && run.status !== 'finalized' && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-full shadow-lg border px-4 py-2"
          style={{
            bottom: '20px',
            background: 'white',
            borderColor: C.plum,
            boxShadow: '0 8px 24px rgba(74, 59, 95, 0.18)',
          }}
          data-testid="pr-bulk-mark-bar"
        >
          <span className="text-[12px] font-semibold" style={{ color: C.ink }}>
            {selected.size} selected
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelected(new Set())}
            data-testid="pr-bulk-clear-btn"
          >
            Clear
          </Button>
          <Button
            size="sm"
            onClick={bulkMarkProcessed}
            disabled={bulkBusy || (run.overlap_finalized_runs || []).length > 0}
            title={(run.overlap_finalized_runs || []).length > 0
              ? 'Blocked — this run overlaps a finalized period. Adjust the date range or unlock the prior run.'
              : undefined}
            className="bg-[#3B6F5D] hover:bg-[#2D5544] text-white disabled:opacity-60"
            data-testid="pr-bulk-mark-btn"
          >
            <CheckCircle2 size={12} className="mr-1" />
            Mark {selected.size} processed
          </Button>
        </div>
      )}

      {/* Validation summary */}
      <ValidationBlock run={run} rows={rows} />

      {/* Iter 193 — Payroll KPI Mini-Dashboard. Airtight bonus trio +
          bucketed pay totals. Shares the same show-session denominator so
          Payroll/Session + Income/Session + Margin/Session never
          double-count a visit. All values recompute on Recalculate. */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="pr-kpi-strip">
        {[
          { key: 'payroll_per_session',  label: 'Payroll / Session', v: usd(summary?.payroll_per_session_cents || 0), tone: C.plum,  title: 'Total payroll ÷ Show + LC + NS sessions. Single per-visit economic number.' },
          { key: 'income_per_session',   label: 'Income / Session',  v: usd(summary?.income_per_session_cents || 0),  tone: C.gold,  title: 'SP collected income ÷ Show + LC + NS sessions. Live from the last import.' },
          { key: 'margin_per_session',   label: 'Margin / Session',  v: usd(summary?.margin_per_session_cents || 0),  tone: (summary?.margin_per_session_cents || 0) >= 0 ? C.ok : C.rose, title: 'Income − Payroll ÷ Show + LC + NS sessions. Net clinician contribution per visit.' },
          { key: 'total_payable_units',  label: 'Total Payable Units', v: fmtUnits(summary?.payable_units_total || 0),      tone: C.ink,   title: 'Sum of payable units across every row (show + HR adjustments including overpayment auto-apply).' },
          { key: 'total_lc_ns',          label: 'Total LC/NS $',       v: usd(summary?.lc_ns_pay_cents || 0),           tone: C.soft,  title: 'Flat LC + NS pay across all rows. Excluded from Payroll/Session numerator.' },
          { key: 'total_bonus',          label: 'Total Bonus $',       v: usd(summary?.bonus_pay_cents || 0),           tone: C.gold,  title: 'Bonus dollars across all rows. Excluded from Payroll/Session numerator.' },
        ].map(t => (
          <div key={t.key}
               title={t.title}
               className="rounded-xl border p-3 bg-white"
               style={{ borderColor: C.border }}
               data-testid={`pr-kpi-${t.key.replace(/_/g, '-')}`}>
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>{t.label}</div>
            <div className="mt-1 font-serif text-2xl tabular-nums" style={{ color: t.tone }}>{t.v}</div>
          </div>
        ))}
      </div>

      {/* Warnings — with click-to-attribute alias assignment */}
      {(run.warnings || []).map((w, i) => (
        <UnmatchedWarning
          key={i}
          warning={w}
          onAttributed={recalc}
        />
      ))}

      {/* Detail table (rows are sorted: Employees first, then Contractors,
          each block alphabetized by last name). */}
      <DetailTable
        rows={sortAndGroupRows(rows)}
        finalized={run.status === 'finalized'}
        overlapBlocked={(run.overlap_finalized_runs || []).length > 0}
        onAdjust={setAdjustment}
        onProcess={processRow}
        onRequestUnlock={setUnlockRow}
        grand={grand}
        selectable={run.status !== 'finalized'}
        selected={selected}
        onToggleRow={toggleRowSelected}
        onToggleAll={toggleAllSelected}
        onOpenTherapist={openTherapistEditor}
        onOpenOverpayments={setOverpaymentRow}
      />

      {overpaymentRow && (
        <OverpaymentDetailDialog
          row={overpaymentRow}
          runId={run.run_id}
          onClose={() => setOverpaymentRow(null)}
          onChanged={() => loadRun(run.run_id)}
        />
      )}

      {editingTherapist && (
        <TherapistForm
          therapist={editingTherapist}
          onCancel={() => setEditingTherapist(null)}
          onSubmit={saveTherapistEdit}
        />
      )}

      {/* Summary entry */}
      <PayrollSummarySection run={run} summary={summary} onSave={saveSummary} saving={savingSummary} />

      {/* Finalize */}
      {(() => {
        const unverifiedProcessed = (rows || []).filter(r =>
          r.processed && !r.gusto_verified && !r.gusto_override
        );
        const verifyAllGusto = async () => {
          if (!window.confirm(`Stamp Gusto override on ${unverifiedProcessed.length} already-processed row(s) so finalize can proceed?\n\nThis is audited — a canned "previously-processed rows lacking verification" reason is stored per row.`)) return;
          try {
            const { data } = await api.post(`/payroll/runs/${run.run_id}/rows/bulk-gusto-verify`);
            toast.success(`${data.stamped} row(s) verified via override`);
            loadRun(run.run_id);
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Verify failed');
          }
        };
        return (
      <div className="flex items-center justify-between rounded-xl border p-4" style={{ borderColor: C.border, background: 'white' }} data-testid="pr-finalize-block">
        <div className="text-[13px]" style={{ color: C.ink2 }}>
          {run.status === 'finalized'
            ? <>Finalized {fmtDate(run.finalized_at)} by {run.finalized_by}</>
            : (run.overlap_finalized_runs || []).length > 0
              ? <>Blocked — date range overlaps a finalized run. Adjust the period or unlock the prior run.</>
              : outstanding.length > 0
                ? <>Blocked — {outstanding.length} therapist(s) still unprocessed: <b>{outstanding.slice(0, 6).join(', ')}{outstanding.length > 6 ? '…' : ''}</b></>
                : unverifiedProcessed.length > 0
                  ? <>Blocked — {unverifiedProcessed.length} processed row(s) missing Gusto verification. Click <b>Verify Gusto</b> to stamp an override (audited) or open each row to run the verify modal.</>
                  : 'All rows processed — ready to finalize.'}
        </div>
        <div className="flex items-center gap-2">
          {run.status !== 'finalized' && unverifiedProcessed.length > 0 && (
            <Button
              variant="outline"
              onClick={verifyAllGusto}
              className="h-10 border-[#B08A3A] text-[#7A5F1F] hover:bg-[#FBF5E8]"
              data-testid="pr-bulk-gusto-verify-btn"
              title={`Stamp gusto_override=true on ${unverifiedProcessed.length} already-processed rows so finalize can proceed. Audited per row.`}
            >
              <CheckCircle2 size={14} className="mr-1.5" /> Verify Gusto ({unverifiedProcessed.length})
            </Button>
          )}
          <Button
            disabled={run.status === 'finalized'
                      || outstanding.length > 0
                      || (run.overlap_finalized_runs || []).length > 0}
            title={(run.overlap_finalized_runs || []).length > 0
              ? 'This run overlaps a finalized period. Adjust the dates or unlock the prior run before finalizing.'
              : undefined}
            onClick={finalize}
            className="h-10 bg-[#3B6F5D] hover:bg-[#2D5544] text-white disabled:opacity-60"
            data-testid="pr-finalize-btn"
          >
            <Lock size={14} className="mr-1.5" /> Finalize payroll period
          </Button>
        </div>
      </div>
        );
      })()}

      {unlockRow && (
        <UnlockDialog
          row={unlockRow}
          onClose={() => setUnlockRow(null)}
          onSubmit={submitUnlock}
        />
      )}

      {gustoActive && (
        <GustoVerifyDialog
          key={gustoRow.therapist_id + ':' + gustoIndex}
          row={gustoRow}
          queueTotal={gustoQueue.length}
          queueIndex={gustoIndex}
          onSubmit={(entries, overrideReason) => submitGustoRow(gustoRow, entries, overrideReason)}
          onProcessed={() => {
            const isLast = gustoIndex + 1 >= gustoQueue.length;
            if (isLast) {
              onGustoQueueDone(gustoQueue.length);
            }
            advanceGustoQueue();
          }}
          onSkip={skipGustoRow}
          onCancel={closeGustoModal}
        />
      )}

      {partialAttPrompt && (
        <PartialAttendanceDialog
          message={partialAttPrompt.message}
          coverage={partialAttPrompt.coverage}
          onProceed={proceedWithPartial}
          onCancel={() => setPartialAttPrompt(null)}
        />
      )}
    </div>
  );
}


function UnmatchedWarning({ warning, onAttributed }) {
  // Locally-managed "resolved" state — the moment HR attributes a name we
  // strike it out and hide it from the tally so the strip visibly shrinks
  // even before the outer recalculate() finishes. onAttributed then triggers
  // a full recompute so the numbers reconcile.
  const [resolved, setResolved] = useState(() => new Set());
  const items = (warning.items || []).filter(it => !resolved.has(it.clinician));
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, 8);
  const hiddenCount = Math.max(items.length - visible.length, 0);

  if (items.length === 0) {
    return (
      <Card className="p-3 border-l-4 border" style={{ borderColor: C.ok, borderLeftColor: C.ok, background: '#EEF7F2' }} data-testid={`pr-warning-cleared-${warning.kind}`}>
        <div className="flex items-center gap-2 text-[12px]" style={{ color: C.ok }}>
          <CheckCircle2 size={13} />
          <span>All flagged clinicians attributed — recalculating&hellip;</span>
        </div>
      </Card>
    );
  }
  return (
    <Card className="p-3 border-l-4 border" style={{ borderColor: C.amber, borderLeftColor: C.amber, background: '#FBF5E9' }} data-testid={`pr-warning-${warning.kind}`}>
      <div className="flex items-start gap-2 text-[12px]" style={{ color: '#8A5D2D' }}>
        <AlertTriangle size={13} style={{ marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{warning.message}</div>
          <div className="mt-0.5 text-[11px]" style={{ color: '#8A5D2D', opacity: 0.85 }}>
            Click any name to attribute it to a therapist &mdash; it'll be saved as an alias so future runs match automatically.
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {visible.map(it => (
              <AttributePill
                key={it.clinician}
                clinician={it.clinician}
                count={it.count}
                onAttributed={() => {
                  setResolved(prev => {
                    const next = new Set(prev);
                    next.add(it.clinician);
                    return next;
                  });
                  onAttributed?.();
                }}
              />
            ))}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="px-2 py-0.5 rounded-full text-[11px] border border-dashed"
                style={{ borderColor: '#B08A3A', color: '#8A5D2D' }}
                data-testid={`pr-warning-showall-${warning.kind}`}
              >
                &hellip; and {hiddenCount} more
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function AttributePill({ clinician, count, onAttributed }) {
  const [open, setOpen] = useState(false);
  const [therapists, setTherapists] = useState(null);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open || therapists) return;
    (async () => {
      try {
        const { data } = await api.get('/payroll/therapists?status=Active');
        setTherapists(data.therapists || []);
      } catch {
        toast.error('Could not load therapist roster');
      }
    })();
  }, [open, therapists]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = therapists || [];
    if (!q) return list.slice(0, 40);
    return list.filter(t =>
      (t.attendance_name || '').toLowerCase().includes(q) ||
      (t.sessions_name || '').toLowerCase().includes(q) ||
      (t.aliases || []).some(a => a.toLowerCase().includes(q))
    ).slice(0, 40);
  }, [therapists, query]);

  const attribute = async (t) => {
    setBusyId(t.therapist_id);
    try {
      await api.post(`/payroll/therapists/${t.therapist_id}/aliases`, { alias: clinician });
      toast.success(`Added "${clinician}" as an alias for ${t.attendance_name}`);
      setOpen(false);
      onAttributed?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not add alias';
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors hover:bg-white"
          style={{ borderColor: '#B08A3A', color: '#8A5D2D', background: 'rgba(255,255,255,0.55)' }}
          data-testid={`pr-attribute-${clinician}`}
        >
          <UserPlus size={11} />
          <span className="tabular-nums">{clinician} · {count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid="pr-attribute-popover">
        <div className="p-2 border-b" style={{ borderColor: C.border }}>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.soft }}>
            Attribute &ldquo;{clinician}&rdquo; to
          </div>
          <div className="flex items-center gap-1.5">
            <Search size={13} style={{ color: C.soft }} />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search therapists&hellip;"
              className="flex-1 text-[13px] outline-none border-0 bg-transparent"
              data-testid="pr-attribute-search"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {therapists == null && (
            <div className="p-3 text-[12px]" style={{ color: C.soft }} data-testid="pr-attribute-loading">Loading&hellip;</div>
          )}
          {therapists != null && filtered.length === 0 && (
            <div className="p-3 text-[12px]" style={{ color: C.soft }} data-testid="pr-attribute-empty">No therapists match &ldquo;{query}&rdquo;</div>
          )}
          {filtered.map(t => (
            <button
              key={t.therapist_id}
              type="button"
              onClick={() => attribute(t)}
              disabled={busyId === t.therapist_id}
              className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-[#F1EDF5] disabled:opacity-50"
              data-testid={`pr-attribute-option-${t.therapist_id}`}
            >
              <div style={{ color: C.ink }}>{t.attendance_name}</div>
              {t.sessions_name && t.sessions_name !== t.attendance_name && (
                <div className="text-[11px]" style={{ color: C.soft }}>Sessions: {t.sessions_name}</div>
              )}
              {(t.aliases?.length || 0) > 0 && (
                <div className="text-[10px]" style={{ color: C.soft }}>Aliases: {t.aliases.join(', ')}</div>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}


function ValidationBlock({ run, rows }) {
  const v = run.validation || {};
  const totalAll = v.total_all || {
    attendance_rows: v.attendance_rows, sessions_rows: v.sessions_rows,
    show_units: v.raw_show, lc_units: v.raw_lc, ns_units: v.raw_ns,
    canceled_units: v.raw_canceled,
  };
  const eligible = v.payroll_eligible || {
    attendance_rows: v.attendance_rows, sessions_rows: v.sessions_rows,
    show_units: v.show_units, lc_units: v.lc_units, ns_units: v.ns_units,
    canceled_units: v.canceled_units,
  };
  // iter 196b — mismatch check compares ONLY the payroll-eligible row
  // against roster-mapped totals. Comparing the "all" row would flag
  // every pay period a salaried SLP or ex-therapist appears in the
  // raw export, which was pure noise.
  const rosterShow = (rows || []).reduce((a, r) => a + (r.show_units || 0), 0);
  const rosterLc   = (rows || []).reduce((a, r) => a + (r.lc_units || 0), 0);
  const rosterNs   = (rows || []).reduce((a, r) => a + (r.ns_units || 0), 0);
  const mismatch =
    (eligible.show_units ?? -1) !== rosterShow ||
    (eligible.lc_units   ?? -1) !== rosterLc ||
    (eligible.ns_units   ?? -1) !== rosterNs;
  return (
    <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="pr-validation">
      <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.gold }}>
        Validation Summary
      </div>
      <div className="mb-2 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }} data-testid="pr-validation-total-label">
        Total Sessions (All)
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-[12px]" data-testid="pr-validation-total-row">
        <Vitem label="Attendance rows" value={int(totalAll.attendance_rows)} />
        <Vitem label="Sessions rows"   value={int(totalAll.sessions_rows)} />
        <Vitem label="Show units"      value={int(totalAll.show_units)} />
        <Vitem label="Late Cancel"     value={int(totalAll.lc_units)} />
        <Vitem label="No Show"         value={int(totalAll.ns_units)} />
        <Vitem label="Canceled (excl.)" value={int(totalAll.canceled_units)} />
      </div>
      <div className="mt-4 mb-2 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.plum }} data-testid="pr-validation-eligible-label">
        Payroll-Eligible Sessions
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-[12px]" data-testid="pr-validation-eligible-row">
        <Vitem label="Attendance rows" value={int(eligible.attendance_rows)} />
        <Vitem label="Sessions rows"   value={int(eligible.sessions_rows)} />
        <Vitem label="Show units"      value={int(eligible.show_units)} highlight />
        <Vitem label="Late Cancel"     value={int(eligible.lc_units)} />
        <Vitem label="No Show"         value={int(eligible.ns_units)} />
        <Vitem label="Canceled (excl.)" value={int(eligible.canceled_units)} />
      </div>
      {mismatch && (
        <div className="mt-3 rounded-lg p-2 text-[12px] flex items-center gap-2" style={{ background: '#FDECEA', color: C.rose }} data-testid="pr-validation-mismatch">
          <AlertTriangle size={13} />
          Mismatch — payroll-eligible shows {eligible.show_units}/{eligible.lc_units}/{eligible.ns_units} (Show/LC/NS)
          but per-therapist rows total {rosterShow}/{rosterLc}/{rosterNs}.
          Check warnings above for unassigned clinicians.
        </div>
      )}
    </Card>
  );
}

function Vitem({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>{label}</div>
      <div className="font-serif text-lg mt-0.5" style={{ color: highlight ? C.plum : C.ink }}>{value}</div>
    </div>
  );
}

function DetailTable({ rows, finalized, overlapBlocked, onAdjust, onProcess, onRequestUnlock, grand, selectable, selected, onToggleRow, onToggleAll, onOpenTherapist, onOpenOverpayments }) {
  const cols = [
    ...(selectable ? [{ k: 'sel', label: '', align: 'center' }] : []),
    { k: 'name', label: 'Therapist', align: 'left' },
    { k: 'chk',  label: 'Processed', align: 'center' },
    { k: 'cat', label: 'Category', align: 'left' },
    { k: 'show', label: 'Show', align: 'right' },
    { k: 'lc',   label: 'LC',   align: 'right' },
    { k: 'ns',   label: 'NS',   align: 'right' },
    { k: 'adj',  label: 'Adjustment', align: 'right' },
    { k: 'pay',  label: 'Payable', align: 'center' },
    { k: 'lcns', label: 'LC/NS $', align: 'right' },
    { k: 'bq',   label: 'Bonus?', align: 'center' },
    { k: 'bo',   label: 'Bonus $', align: 'right' },
    { k: 'c507', label: '92507', align: 'center' },
    { k: 'cEval', label: '92523/92610 (Eval)', align: 'center' },
    { k: 'c526', label: '92526', align: 'center' },
  ];
  const allSelectable = rows.filter(r => !r.processed).map(r => r.therapist_id);
  const allChecked = selectable && allSelectable.length > 0 && allSelectable.every(id => selected?.has(id));
  // Count rows per group for section-header captions.
  const groupCounts = rows.reduce((acc, r) => {
    const g = CATEGORY_GROUP(r.category_snapshot);
    acc[g] = (acc[g] || 0) + 1;
    return acc;
  }, {});
  // Subtotal each numeric field per group so we can emit an "Employees
  // subtotal" / "Contractors subtotal" band before the grand total.
  const emptySub = () => ({
    show: 0, lc: 0, ns: 0, adj: 0, payable: 0, lcns: 0, bonus: 0,
    cpt507: 0, cptEval: 0, cpt526: 0,
  });
  const subtotals = { Employee: emptySub(), Contractor: emptySub() };
  rows.forEach(r => {
    const g = CATEGORY_GROUP(r.category_snapshot);
    const s = subtotals[g];
    s.show    += r.show_units || 0;
    s.lc      += r.lc_units || 0;
    s.ns      += r.ns_units || 0;
    s.adj     += r.session_adjustment || 0;
    s.payable += r.payable_units || 0;
    s.lcns    += r.lc_ns_comp_cents || 0;
    s.bonus   += r.bonus_amount_cents || 0;
    s.cpt507  += r.cpt_92507_units || 0;
    s.cptEval += (r.cpt_92523_units || 0) + (r.cpt_92610_units || 0);
    s.cpt526  += r.cpt_92526_units || 0;
  });
  const colSpan = cols.length;
  return (
    <Card className="rounded-2xl border overflow-hidden" style={{ borderColor: C.border }}>
      <div className="overflow-auto">
        <table className="w-full text-[13px]" data-testid="pr-detail-table">
          <thead className="text-[10px] uppercase tracking-widest sticky top-0 z-10"
                 style={{ background: C.paper, color: C.soft }}>
            <tr>
              {cols.map(c => {
                const emphasized = c.k === 'pay' || c.k === 'lcns';
                return (
                  <th key={c.k}
                      className={`py-3 px-3 text-${c.align} whitespace-nowrap ${emphasized ? 'font-extrabold text-[12px]' : ''}`}
                      style={emphasized ? { color: C.plum } : undefined}>
                    {c.k === 'sel' ? (
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={() => onToggleAll && onToggleAll(allSelectable, !allChecked)}
                        className="h-3.5 w-3.5"
                        disabled={allSelectable.length === 0}
                        data-testid="pr-bulk-toggle-all"
                      />
                    ) : c.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const currentGroup = CATEGORY_GROUP(r.category_snapshot);
              const prevGroup = i > 0 ? CATEGORY_GROUP(rows[i - 1].category_snapshot) : null;
              const showHeader = i === 0 || currentGroup !== prevGroup;
              return (
                <React.Fragment key={r.therapist_id}>
                  {showHeader && (
                    <tr data-testid={`pr-group-header-${currentGroup.toLowerCase()}`}>
                      <td colSpan={colSpan}
                          className="py-2 px-3 text-[10px] uppercase tracking-[0.18em] font-bold"
                          style={{ background: '#F1EEF4', color: C.soft, borderTop: `1px solid ${C.border}` }}>
                        {currentGroup === 'Employee' ? 'Employees' : 'Contractors'} ({groupCounts[currentGroup] || 0})
                      </td>
                    </tr>
                  )}
                  <tr
                    className={`border-t ${i % 2 ? 'bg-white' : 'bg-[#FDFCFA]'} ${r.processed ? 'opacity-95' : ''}`}
                    style={{ borderColor: '#F0EDF2' }}
                    data-testid={`pr-row-${r.therapist_id}`}>
                    {selectable && (
                      <td className="py-2.5 px-3 text-center">
                        <input
                      type="checkbox"
                      checked={selected?.has(r.therapist_id) || false}
                      disabled={r.processed || finalized}
                      onChange={() => onToggleRow && onToggleRow(r.therapist_id)}
                      className="h-3.5 w-3.5"
                      data-testid={`pr-bulk-sel-${r.therapist_id}`}
                    />
                  </td>
                )}
                <td className="py-2.5 px-3" style={{ color: C.ink, fontWeight: 500 }}>
                  {onOpenTherapist ? (
                    <button
                      type="button"
                      onClick={() => onOpenTherapist(r.therapist_id)}
                      className="text-left hover:underline focus:outline-none focus:underline"
                      style={{ color: C.plum, fontWeight: 600, cursor: 'pointer' }}
                      title="Open therapist setup"
                      data-testid={`pr-row-name-${r.therapist_id}`}
                    >
                      {r.therapist_name_snapshot}
                    </button>
                  ) : (
                    <span>{r.therapist_name_snapshot}</span>
                  )}
                  {r.therapist_type_snapshot && r.therapist_type_snapshot !== 'Other' && (
                    <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ background: r.therapist_type_snapshot === 'SLP' ? '#EFE9F5' : '#E4EEE9',
                                   color: r.therapist_type_snapshot === 'SLP' ? C.plum : C.ok }}>
                      {r.therapist_type_snapshot}
                    </span>
                  )}
                  {/* Overpayment chip — only on draft runs. Click opens
                      the per-row detail with Client + DOS + status flip
                      so HR can notify the therapist and apply/hold. */}
                  {(r.overpayment_adjustment_cents || 0) > 0 && !finalized && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenOverpayments && onOpenOverpayments(r); }}
                      className="ml-1.5 text-[10px] px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1 hover:bg-[#F8E5E5]"
                      style={{ background: '#FBE8E8', color: '#8A3A3A', border: '1px solid #D5B6B6' }}
                      data-testid={`pr-overpayment-chip-${r.therapist_id}`}
                      title={`${r.overpayment_adjustment_count} unit${r.overpayment_adjustment_count === 1 ? '' : 's'} to deduct — click for details`}
                    >
                      <DollarSign size={10} strokeWidth={2.5} />
                      −{usd(r.overpayment_adjustment_cents)}
                      <span className="text-[9px] font-semibold opacity-80">(−{r.overpayment_adjustment_count}u)</span>
                    </button>
                  )}
                </td>
                <td className="py-2.5 px-3 text-center">
                  {r.processed
                    ? <button
                        onClick={() => onRequestUnlock && onRequestUnlock(r)}
                        disabled={finalized}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold text-white bg-[#3B6F5D] disabled:opacity-70"
                        title="Click to unlock (reason required)"
                        data-testid={`pr-processed-${r.therapist_id}`}
                      ><CheckCircle2 size={14} /> Processed</button>
                    : <button
                        onClick={() => onProcess(r)}
                        disabled={finalized || overlapBlocked}
                        title={overlapBlocked
                          ? 'Blocked — this run overlaps a finalized period. Adjust the date range or unlock the prior run.'
                          : undefined}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border-2 text-[#4A3B5F] hover:bg-[#EFE9F5] disabled:opacity-70"
                        style={{ borderColor: C.plum }}
                        data-testid={`pr-mark-${r.therapist_id}`}
                      >Mark processed</button>}
                </td>
                <td className="py-2.5 px-3 text-[11px]" style={{ color: C.soft }}>{r.category_snapshot}</td>
                <td className="py-2.5 px-3 text-right tabular-nums" style={{ color: C.plum, fontWeight: 600 }}>{int(r.show_units)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{int(r.lc_units)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{int(r.ns_units)}</td>
                <td className="py-2.5 px-3 text-right">
                  <input
                    type="number"
                    className="w-16 text-right rounded border px-2 py-1 text-sm disabled:bg-slate-100 disabled:opacity-70"
                    style={{ borderColor: C.border }}
                    defaultValue={r.session_adjustment || 0}
                    disabled={finalized || r.processed}
                    onBlur={e => onAdjust(r.therapist_id, e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    data-testid={`pr-adj-${r.therapist_id}`}
                  />
                </td>
                <td className="py-2.5 px-3 text-center tabular-nums font-bold text-[14px]">{fmtUnits(r.payable_units)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums font-bold text-[14px]">{usd(r.lc_ns_comp_cents)}</td>
                <td className="py-2.5 px-3 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    r.bonus_qualified ? 'bg-[#B08A3A] text-white' : 'bg-slate-100 text-slate-500'
                  }`}>{r.bonus_qualified ? 'YES' : 'no'}</span>
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums" style={{ color: r.bonus_amount_cents > 0 ? C.gold : C.soft }}>{usd(r.bonus_amount_cents)}</td>
                <td className="py-2.5 px-3 text-center tabular-nums" style={{ color: C.soft }}>{int(r.cpt_92507_units)}</td>
                <td className="py-2.5 px-3 text-center tabular-nums" style={{ color: C.soft }}>{int((r.cpt_92523_units || 0) + (r.cpt_92610_units || 0))}</td>
                <td className="py-2.5 px-3 text-center tabular-nums" style={{ color: C.soft }}>{int(r.cpt_92526_units)}</td>
                  </tr>
                  {(() => {
                    const nextGroup = i + 1 < rows.length ? CATEGORY_GROUP(rows[i + 1].category_snapshot) : null;
                    const isLastOfGroup = nextGroup !== currentGroup;
                    if (!isLastOfGroup) return null;
                    const s = subtotals[currentGroup];
                    return (
                      <tr className="border-t bg-[#F7F3FA] font-semibold"
                          style={{ borderColor: C.border }}
                          data-testid={`pr-subtotal-${currentGroup.toLowerCase()}`}>
                        {selectable && <td />}
                        <td className="py-2 px-3 text-[11px] uppercase tracking-widest" style={{ color: C.plum }}>
                          {currentGroup === 'Employee' ? 'Employees' : 'Contractors'} subtotal
                        </td>
                        <td />
                        <td />
                        <td className="py-2 px-3 text-right tabular-nums" style={{ color: C.plum }}>{int(s.show)}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{int(s.lc)}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{int(s.ns)}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{int(s.adj)}</td>
                        <td className="py-2 px-3 text-center tabular-nums font-bold text-[13px]">{int(s.payable)}</td>
                        <td className="py-2 px-3 text-right tabular-nums font-bold text-[13px]">{usd(s.lcns)}</td>
                        <td />
                        <td className="py-2 px-3 text-right tabular-nums" style={{ color: C.gold }}>{usd(s.bonus)}</td>
                        <td className="py-2 px-3 text-center tabular-nums">{int(s.cpt507)}</td>
                        <td className="py-2 px-3 text-center tabular-nums">{int(s.cptEval)}</td>
                        <td className="py-2 px-3 text-center tabular-nums">{int(s.cpt526)}</td>
                      </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
            {/* Grand total */}
            <tr className="border-t-2 bg-[#EFE9F5] font-bold" style={{ borderColor: C.plum }} data-testid="pr-grand-total">
              {selectable && <td />}
              <td className="py-3 px-3" style={{ color: C.plum }}>GRAND TOTAL</td>
              <td />
              <td />
              <td className="py-3 px-3 text-right tabular-nums" style={{ color: C.plum }}>{int(grand.show)}</td>
              <td className="py-3 px-3 text-right tabular-nums">{int(grand.lc)}</td>
              <td className="py-3 px-3 text-right tabular-nums">{int(grand.ns)}</td>
              <td className="py-3 px-3 text-right tabular-nums">{int(grand.adj)}</td>
              <td className="py-3 px-3 text-center tabular-nums font-bold text-[14px]">{int(grand.payable)}</td>
              <td className="py-3 px-3 text-right tabular-nums font-bold text-[14px]">{usd(grand.lcns)}</td>
              <td />
              <td className="py-3 px-3 text-right tabular-nums" style={{ color: C.gold }}>{usd(grand.bonus)}</td>
              <td className="py-3 px-3 text-center tabular-nums">{int(grand.cpt507)}</td>
              <td className="py-3 px-3 text-center tabular-nums">{int(grand.cptEval)}</td>
              <td className="py-3 px-3 text-center tabular-nums">{int(grand.cpt526)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PayrollSummarySection({ run, summary, onSave, saving }) {
  const finalized = run.status === 'finalized';
  const [form, setForm] = useState({
    salaried_gross: '', salaried_notes: '',
    hourly_gross: '', hourly_notes: '',
    contractor: '', contractor_notes: '',
    overseas_contractor: '', overseas_notes: '',
    other_admin: '', other_notes: '',
    sp_income: '',
  });
  useEffect(() => {
    if (!summary) return;
    setForm({
      salaried_gross: summary.salaried_cents ? (summary.salaried_cents / 100).toFixed(2) : '',
      salaried_notes: summary.salaried_notes || '',
      hourly_gross: summary.hourly_cents ? (summary.hourly_cents / 100).toFixed(2) : '',
      hourly_notes: summary.hourly_notes || '',
      contractor: summary.contractor_cents ? (summary.contractor_cents / 100).toFixed(2) : '',
      contractor_notes: summary.contractor_notes || '',
      overseas_contractor: summary.overseas_contractor_cents ? (summary.overseas_contractor_cents / 100).toFixed(2) : '',
      overseas_notes: summary.overseas_notes || '',
      other_admin: summary.other_admin_cents ? (summary.other_admin_cents / 100).toFixed(2) : '',
      other_notes: summary.other_notes || '',
      sp_income: summary.sp_income_cents ? (summary.sp_income_cents / 100).toFixed(2) : '',
    });
  }, [summary]);

  // ── Live KPIs — mirror backend `_compute_summary_kpis` so HR sees the
  // math update as they type, before hitting Save. Unit totals still come
  // from the server (they're derived from run rows, not user input).
  const live = useMemo(() => {
    const toCents = (s) => {
      const n = parseFloat(s);
      return Number.isFinite(n) ? Math.round(n * 100) : 0;
    };
    const sal = toCents(form.salaried_gross);
    const hr  = toCents(form.hourly_gross);
    const ct  = toCents(form.contractor);
    const ov  = toCents(form.overseas_contractor);
    const oa  = toCents(form.other_admin);
    const totalPayroll = sal + hr + ct + ov + oa;
    const income = toCents(form.sp_income);
    const showU = Number(summary?.total_show_units || 0);
    const totalU = Number(summary?.total_units || 0);
    const margin = income - totalPayroll;
    return {
      total_payroll_cents: totalPayroll,
      sp_income_cents: income,
      payroll_per_unit_cents: totalU ? Math.round(totalPayroll / totalU) : 0,
      income_per_unit_cents: totalU ? Math.round(income / totalU) : 0,
      margin_per_unit_cents: totalU ? Math.round(margin / totalU) : 0,
      margin_cents: margin,
      margin_pct: income ? (margin / income) : 0,
    };
  }, [form, summary]);

  // Flag whenever the entered form has drifted from the saved summary so we
  // can tint the KPI panel "preview" until HR clicks Save.
  const dirty = useMemo(() => {
    if (!summary) return live.total_payroll_cents !== 0 || live.sp_income_cents !== 0;
    return (
      live.total_payroll_cents !== (summary.total_payroll_cents || 0) ||
      live.sp_income_cents      !== (summary.sp_income_cents || 0)
    );
  }, [live, summary]);

  const cats = [
    { key: 'salaried_gross', notes: 'salaried_notes', label: 'Salaried Employee Gross' },
    { key: 'hourly_gross',   notes: 'hourly_notes',   label: 'Unit-Based Employee Gross' },
    { key: 'contractor',     notes: 'contractor_notes', label: 'Contractor Payments' },
    { key: 'overseas_contractor', notes: 'overseas_notes', label: 'Overseas Contractor' },
    { key: 'other_admin',    notes: 'other_notes', label: 'Other Admin / Misc' },
  ];

  return (
    <Card className="p-5 border" style={{ borderColor: C.border }} data-testid="pr-summary-block">
      <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.gold }}>
        Payroll summary & KPIs
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {/* Left: entries */}
        <div className="space-y-3">
          {cats.map(c => (
            <div key={c.key} className="grid grid-cols-[1fr_140px] gap-2 items-start">
              <div>
                <label className="text-[11px] font-semibold" style={{ color: C.ink }}>{c.label}</label>
                <Textarea
                  value={form[c.notes] || ''} placeholder="Notes (optional)"
                  onChange={e => setForm(f => ({ ...f, [c.notes]: e.target.value }))}
                  disabled={finalized}
                  className="mt-1 min-h-[46px] text-sm"
                  data-testid={`pr-summary-${c.key}-notes`}
                />
              </div>
              <div>
                <label className="text-[11px] text-[#685B72]">Amount ($)</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form[c.key] || ''}
                  onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))}
                  disabled={finalized}
                  className="mt-1 text-sm text-right"
                  data-testid={`pr-summary-${c.key}`}
                />
              </div>
            </div>
          ))}
          <div className="grid grid-cols-[1fr_140px] gap-2 items-start pt-2 border-t" style={{ borderColor: C.border }}>
            <div>
              <label className="text-[11px] font-semibold" style={{ color: C.plum }}>Income (this pay period)</label>
              <div className="text-[11px]" style={{ color: C.soft }}>
                Enter the collected income for this bi-weekly window. The
                monthly SP income report is prorated by HR — this field
                drives Margin ($ / %) and Income per session.
              </div>
            </div>
            <Input
              type="number" step="0.01" min="0"
              value={form.sp_income || ''}
              onChange={e => setForm(f => ({ ...f, sp_income: e.target.value }))}
              disabled={finalized}
              className="text-sm text-right"
              data-testid="pr-summary-sp-income"
            />
          </div>
          <Button
            onClick={() => onSave(form)}
            disabled={finalized || saving}
            className="mt-3 bg-[#4A3B5F] hover:bg-[#2D2833] text-white"
            data-testid="pr-summary-save"
          >{saving ? 'Saving…' : dirty ? 'Save summary (unsaved changes)' : 'Save summary'}</Button>
        </div>
        {/* Right: derived — LIVE from form so HR sees KPIs move as they type */}
        <div className={`rounded-xl border p-4 transition-colors ${dirty ? 'ring-1 ring-[#B08A3A]' : ''}`}
             style={{ borderColor: C.border, background: C.paper }} data-testid="pr-summary-kpis">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.plum }}>KPIs {dirty ? '(preview — unsaved)' : '(saved)'}</div>
            {dirty && <div className="text-[10px]" style={{ color: C.gold }}>Click Save to persist</div>}
          </div>
          <KpiRow k="Total Payroll (all-in)" v={usd(live.total_payroll_cents)} big />
          <KpiRow k="Total Units — org-wide (Show+LC+NS)" v={int(summary?.total_units)} />
          <KpiRow k="Total Show Units — org-wide" v={int(summary?.total_show_units)} />
          {summary?.payroll_units != null && (
            <div className="text-[10px] pl-2 -mt-1 mb-1" style={{ color: C.soft }}>
              of which {int(summary.payroll_units)} on payable payroll ({int(summary.payroll_show_units)} show)
            </div>
          )}
          <KpiRow k="Payroll / Session" v={usd(live.payroll_per_unit_cents)} />
          <div className="my-2 border-t" style={{ borderColor: C.border }} />
          <KpiRow k="Income (this period)" v={usd(live.sp_income_cents)} />
          <KpiRow k="Income / Session" v={usd(live.income_per_unit_cents)} />
          <div className="my-2 border-t" style={{ borderColor: C.border }} />
          <KpiRow k="Margin ($)" v={usd(live.margin_cents)} big
                  tone={live.margin_cents >= 0 ? C.ok : C.rose} />
          <KpiRow k="Margin / Session" v={usd(live.margin_per_unit_cents)}
                  tone={live.margin_per_unit_cents >= 0 ? C.ok : C.rose} />
          <KpiRow k="Margin (%)" v={pct(live.margin_pct || 0)} />
          <KpiRow k="LC/NS Rate (%)" v={pct(summary?.lc_ns_rate_pct || 0)} />
        </div>
      </div>
    </Card>
  );
}

function KpiRow({ k, v, big, tone }) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <div className="text-[12px]" style={{ color: C.soft }}>{k}</div>
      <div className={`tabular-nums font-serif ${big ? 'text-lg' : 'text-sm'}`} style={{ color: tone || C.ink }}>{v}</div>
    </div>
  );
}

function UnlockDialog({ row, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="pr-unlock-dialog">
        <DialogHeader>
          <DialogTitle>Unlock {row.therapist_name_snapshot}</DialogTitle>
          <DialogDescription>Reason will be logged in the payroll audit trail.</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="Why is this row being unlocked?"
                  className="min-h-[80px]" data-testid="pr-unlock-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={reason.trim().length < 3}
                  onClick={() => onSubmit(reason.trim())}
                  className="bg-[#4A3B5F] hover:bg-[#2D2833] text-white"
                  data-testid="pr-unlock-submit">
            <Unlock size={14} className="mr-1" /> Unlock row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// GustoVerifyDialog — HR keys what they actually entered into Gusto for a
// therapist row. Server compares against app-computed values. Any mismatch
// requires an override reason (≥3 chars). Blank inputs by design so HR
// can't lazily rubber-stamp a run.
//
// The 6 verified fields are:
//   payable_units · lc_ns_dollars · bonus_dollars · 92507 · 92523 · 92526
function GustoVerifyDialog({ row, queueTotal, queueIndex, onSubmit, onProcessed, onSkip, onCancel }) {
  const isQueue = queueTotal > 1;
  const isCptContractor = String(row.category_snapshot || '').toLowerCase() === 'contractor-cpt';
  const [values, setValues] = useState({
    payable_units: '',
    lc_ns_dollars: '',
    bonus_dollars: '',
    cpt_92507_units: '',
    cpt_eval_units: '',
    cpt_92526_units: '',
    cpt_pay_dollars: '',
  });
  const [mismatches, setMismatches] = useState([]);
  const [override, setOverride] = useState('');
  const [busy, setBusy] = useState(false);
  const setV = (k, v) => setValues(vs => ({ ...vs, [k]: v }));

  // Base 3 fields for everyone.
  const baseFields = [
    { key: 'payable_units', label: 'Payable units',  app: row.payable_units,                    type: 'int',   api: 'payable_units' },
    { key: 'lc_ns_dollars', label: 'LC/NS $',        app: (row.lc_ns_comp_cents || 0) / 100,    type: 'money', api: 'lc_ns_cents' },
    { key: 'bonus_dollars', label: 'Bonus $',        app: (row.bonus_amount_cents || 0) / 100,  type: 'money', api: 'bonus_cents' },
  ];
  // CPT-paid contractors also verify per-CPT counts + total combined pay.
  // 92523 + 92610 are surfaced as one combined "92523/92610 count (Eval)"
  // field — HR keys the SUM of what appeared in Gusto for those two codes.
  const cptFields = [
    { key: 'cpt_92507_units', label: '92507 count',                app: row.cpt_92507_units,                                                    type: 'int',   api: 'cpt_92507_units' },
    { key: 'cpt_eval_units',  label: '92523/92610 count (Eval)',   app: (row.cpt_92523_units || 0) + (row.cpt_92610_units || 0),               type: 'int',   api: 'cpt_eval_units' },
    { key: 'cpt_92526_units', label: '92526 count',                app: row.cpt_92526_units,                                                    type: 'int',   api: 'cpt_92526_units' },
    { key: 'cpt_pay_dollars', label: 'CPT pay $ (total)',
      app: ((row.therapy_comp_cents || 0) + (row.eval_comp_cents || 0)) / 100,
      type: 'money', api: 'cpt_pay_cents' },
  ];
  const fieldsInfo = isCptContractor ? [...baseFields, ...cptFields] : baseFields;
  const requiredKeys = fieldsInfo.map(f => f.key);
  const allFilled = requiredKeys.every(k => values[k] !== '' && values[k] !== null && !Number.isNaN(Number(values[k])));

  const mismatchApiFields = new Set(mismatches.map(m => m.field));
  const fieldMismatched = (info) => mismatchApiFields.has(info.api);
  const needsOverride = mismatches.length > 0;

  const doSubmit = async () => {
    setBusy(true);
    try {
      // Only send the fields we're actually verifying for this category.
      const payload = {};
      requiredKeys.forEach(k => { payload[k] = values[k]; });
      await onSubmit(payload, needsOverride ? override.trim() : null);
      setMismatches([]); setOverride('');
      onProcessed();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.code === 'gusto_mismatch') {
        setMismatches(detail.mismatches || []);
      } else {
        toast.error('Mark-processed failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const submitDisabled = busy || !allFilled || (needsOverride && override.trim().length < 3);
  const buttonLabel = needsOverride
    ? 'Confirm override & process'
    : (isQueue ? `Verify & mark processed (${queueIndex + 1} of ${queueTotal})` : 'Verify & mark processed');

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="pr-gusto-verify-dialog">
        <DialogHeader>
          <DialogTitle>
            Verify Gusto entry — {row.therapist_name_snapshot}
            {isQueue && (
              <span className="ml-2 text-[11px] font-normal px-2 py-0.5 rounded-full"
                    style={{ background: '#EFE9F5', color: C.plum }}>
                {queueIndex + 1} / {queueTotal}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Enter exactly what you keyed into Gusto for this therapist. We'll compare against
            the app-calculated values and flag any discrepancy.
            {isCptContractor && (
              <span className="block mt-1 text-[11px]" style={{ color: C.plum }}>
                <b>Contractor-CPT</b> — verify the CPT counts and total CPT $$ they get paid.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {fieldsInfo.map(info => {
            const isMoney = info.type === 'money';
            const bad = fieldMismatched(info);
            return (
              <div key={info.key}
                   className="grid grid-cols-[1fr_140px] gap-3 items-center rounded-lg border px-3 py-2"
                   style={{
                     background: bad ? '#FBEBE9' : 'white',
                     borderColor: bad ? '#D8776A' : C.border,
                   }}
                   data-testid={`pr-gusto-field-${info.key}`}>
                <div>
                  <div className="text-[12px] font-semibold" style={{ color: C.ink }}>{info.label}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: bad ? '#8A3A3A' : C.soft }}>
                    {bad
                      ? <>App says <b>{isMoney ? usd(info.app * 100) : int(info.app)}</b> — you entered <b>{isMoney ? usd(Number(values[info.key] || 0) * 100) : int(values[info.key])}</b></>
                      : 'Type what you entered into Gusto'}
                  </div>
                </div>
                <Input
                  type="number"
                  step={isMoney ? '0.01' : '1'}
                  min="0"
                  value={values[info.key]}
                  onChange={e => setV(info.key, e.target.value)}
                  placeholder={isMoney ? '0.00' : '0'}
                  className="text-right text-sm"
                  data-testid={`pr-gusto-input-${info.key}`}
                />
              </div>
            );
          })}
        </div>

        {needsOverride && (
          <div className="mt-2">
            <label className="text-[11px] uppercase tracking-widest font-bold" style={{ color: C.rose }}>
              Override reason (required, ≥3 chars)
            </label>
            <Textarea
              value={override}
              onChange={e => setOverride(e.target.value)}
              placeholder="Explain why the app values are wrong or why the Gusto entry differs — audited."
              className="min-h-[70px] mt-1"
              data-testid="pr-gusto-override-reason"
            />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="pr-gusto-cancel">
            Cancel
          </Button>
          {isQueue && (
            <Button variant="outline" onClick={onSkip} disabled={busy} data-testid="pr-gusto-skip">
              Skip this therapist
            </Button>
          )}
          <Button
            onClick={doSubmit}
            disabled={submitDisabled}
            className={needsOverride ? 'bg-[#8A3A3A] hover:bg-[#6E2E2E] text-white' : 'bg-[#3B6F5D] hover:bg-[#2D5544] text-white'}
            data-testid="pr-gusto-submit"
          >
            {busy ? 'Submitting…' : buttonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════ 2. Archive tab ═══════════════

// ═══════════════ Overpayment adjustments — iter 355 ═══════════════

function OverpaymentBanner({ cents, count, therapists, orphans, onApplyAll }) {
  // Top-of-draft banner. Shows total pending overpayment $, how many
  // adjustments across how many therapists, and a one-click auto-apply
  // button. HR can still Hold/Waive individual rows from the chip.
  // ALSO surfaces orphan adjustments (therapists not in this draft) so
  // HR can see every open flag — not just the ones for current staff.
  const orphanCount = orphans?.therapist_count || 0;
  const orphanItems = orphans?.orphans?.reduce((a, o) => a + (o.count || 0), 0) || 0;
  const orphanCents = orphans?.total_cents || 0;
  const [orphanOpen, setOrphanOpen] = useState(false);
  return (
    <div
      className="rounded-xl border p-4 flex items-start gap-3"
      style={{ borderColor: '#8A3A3A', background: '#FBECEC', color: '#5F1F1F' }}
      data-testid="pr-overpayment-banner"
    >
      <DollarSign size={18} style={{ color: '#8A3A3A', marginTop: 2 }} />
      <div className="flex-1">
        {(cents || 0) > 0 && (
          <div className="font-semibold text-[13px]">
            Overpayment Adjustments pending — {usd(cents)} across {count} session{count === 1 ? '' : 's'} ({therapists} therapist{therapists === 1 ? '' : 's'}).
          </div>
        )}
        <div className="text-[12px] mt-1 opacity-90">
          Prior-period sessions were paid as Show but have since been changed in SimplePractice (Cancel / Deleted / Late canceled / No show) OR had more 92507 units billed than Shows attended. Click a therapist's red chip in the table to see the Client + DOS so you can notify them, then <b>Apply to this draft</b> to deduct it from the next check.
        </div>
        {orphanCount > 0 && (
          <button
            type="button"
            onClick={() => setOrphanOpen(true)}
            className="mt-2 text-[12px] font-semibold underline decoration-dotted hover:text-[#8A3A3A]"
            data-testid="pr-overpayment-orphan-link"
          >
            + {orphanItems} more {orphanItems === 1 ? 'adjustment' : 'adjustments'} ({usd(orphanCents)}) for {orphanCount} therapist{orphanCount === 1 ? '' : 's'} not in this draft — review
          </button>
        )}
      </div>
      {(cents || 0) > 0 && (
        <Button
          onClick={onApplyAll}
          className="bg-[#8A3A3A] hover:bg-[#6E2E2E] text-white h-9 rounded-full text-[12px] font-semibold px-4"
          data-testid="pr-overpayment-apply-all"
        >
          Apply all to draft
        </Button>
      )}
      {orphanOpen && (
        <OrphanOverpaymentsDialog orphans={orphans?.orphans || []} onClose={() => setOrphanOpen(false)} />
      )}
    </div>
  );
}

function OrphanOverpaymentsDialog({ orphans, onClose }) {
  // Read-only drill-down for Pending overpayment adjustments whose
  // therapist is NOT in the current draft's rows (e.g. on PTO this
  // period, no sessions worked, or no longer with the practice). HR
  // acts on them via the main /payroll/overpayments admin page.
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="pr-overpayment-orphan-dialog">
        <DialogHeader>
          <DialogTitle>Overpayments for therapists not in this draft</DialogTitle>
          <DialogDescription>
            These Pending adjustments belong to therapists whose rows aren't in this draft (likely no sessions worked this period). They stay flagged until the therapist appears in a draft run and the auto-apply can net them, or you Hold/Waive them individually.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border overflow-hidden max-h-[480px] overflow-y-auto" style={{ borderColor: C.border }}>
          <table className="w-full text-[12px]">
            <thead style={{ background: '#FBECEC', color: '#5F1F1F' }} className="sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Therapist</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">DOS</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-right px-3 py-2">Deduction</th>
              </tr>
            </thead>
            <tbody>
              {orphans.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center" style={{ color: C.soft }}>No orphan adjustments.</td></tr>
              ) : orphans.flatMap(o => (o.items || []).map(it => (
                <tr key={it.adj_id} className="border-t" style={{ borderColor: C.border }}>
                  <td className="px-3 py-2 font-semibold" style={{ color: C.ink }}>{o.therapist_name}</td>
                  <td className="px-3 py-2">{it.client || '—'}</td>
                  <td className="px-3 py-2 tabular-nums">{it.dos}</td>
                  <td className="px-3 py-2 text-[11px]">
                    {it.detection_kind === 'cpt_overshoot' ? (
                      <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#EFE9F5', color: C.plum }}>CPT overshoot</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#FBECEC', color: '#8A3A3A' }}>Status flip</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold" style={{ color: '#8A3A3A' }}>−{usd(it.cents)}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverpaymentDetailDialog({ row, runId, onClose, onChanged }) {
  // Lists every Pending adjustment for a single therapist. HR actions:
  //  • Hold   — exclude from the auto-apply pass (keeps the flag open)
  //  • Waive  — permanently remove (reason required)
  //  • Apply  — immediately apply ALL this therapist's items to the draft
  // The dialog pulls live items from /api/payroll/overpayments so stale
  // chip data doesn't desync with a background scan.
  const [items, setItems] = useState(row.overpayment_items || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Compute preview_fractional_units client-side at the DRAFT rate so
    // the per-item '−0.XX units' line stays visible after the refetch
    // (the raw /overpayments list endpoint doesn't hydrate it).
    const draftRateCents = Math.round(Number(row.therapy_session_rate_snapshot || 0) * 100);
    api.get('/payroll/overpayments', { params: { therapist_id: row.therapist_id, status: 'Pending' } })
      .then(({ data }) => setItems((data.items || []).map(a => ({
        ...a,
        cents: a.overpayment_cents,
        preview_fractional_units: draftRateCents > 0
          ? Math.round((a.overpayment_cents / draftRateCents) * 100) / 100
          : 0,
        draft_rate_cents: draftRateCents,
      }))))
      .catch(() => {});
  }, [row.therapist_id, row.therapy_session_rate_snapshot]);

  const totalCents = items.reduce((a, i) => a + (i.cents || 0), 0);

  const act = async (adjId, newStatus) => {
    const reason = (newStatus === 'Hold' || newStatus === 'Waived')
      ? window.prompt(`Reason for marking this adjustment ${newStatus}? (required)`)
      : '';
    if ((newStatus === 'Hold' || newStatus === 'Waived') && !reason) return;
    setBusy(true);
    try {
      await api.patch(`/payroll/overpayments/${adjId}`, { status: newStatus, reason });
      setItems(prev => prev.filter(i => i.adj_id !== adjId));
      toast.success(`Marked ${newStatus}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const applyAllForTherapist = async () => {
    if (!window.confirm(
      `Apply ${items.length} adjustment${items.length === 1 ? '' : 's'} totaling ${usd(totalCents)} to ${row.therapist_name_snapshot}'s row in this draft?`
    )) return;
    setBusy(true);
    try {
      // There's no per-therapist auto-apply endpoint — call the run-level
      // one. Pending adjustments for other therapists in the run will
      // also apply; that's the agreed default behaviour (auto by default
      // with HR able to Hold/Waive individuals first).
      const { data } = await api.post(`/payroll/overpayments/auto-apply/${runId}`);
      toast.success(`Applied ${data.applied} row(s) · ${usd(data.cents_total)} deducted`);
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="pr-overpayment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign size={18} style={{ color: '#8A3A3A' }} />
            Overpayment Adjustments — {row.therapist_name_snapshot}
          </DialogTitle>
          <DialogDescription>
            Payroll paid more Shows than SimplePractice has today for these pay periods. Applying deducts the lost Shows from payable units and credits any LC/NS still owed. Hold keeps it out of Apply; Waive clears it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {items.length === 0 && (
            <div className="px-3 py-6 text-center text-[12px]" style={{ color: C.soft }}>No pending adjustments.</div>
          )}
          {items.map(it => <OverpaymentItemCard key={it.adj_id} it={it} busy={busy} onAct={act} />)}
          {items.length > 0 && (
            <div className="flex justify-end gap-3 px-3 py-2 rounded-lg text-[13px] font-bold" style={{ background: '#FBECEC', color: '#5F1F1F' }} data-testid="pr-ovp-total">
              Total deduction <span className="tabular-nums">−{usd(totalCents)}</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="pr-ovp-close">Close</Button>
          <Button
            type="button"
            variant="outline"
            disabled={busy || items.length === 0}
            onClick={async () => {
              try {
                const { data } = await api.get(`/payroll/overpayments/explanation/${row.therapist_id}`, {
                  params: { run_id: runId },
                });
                if (data && data.text) {
                  await navigator.clipboard.writeText(data.text);
                  toast.success(`Copied explanation for ${data.therapist_name || row.therapist_name_snapshot}`);
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Could not build explanation');
              }
            }}
            data-testid="pr-ovp-copy-explanation"
          >
            <Copy size={13} className="mr-1" /> Copy explanation for therapist
          </Button>
          <Button
            onClick={applyAllForTherapist}
            disabled={busy || items.length === 0}
            className="bg-[#8A3A3A] hover:bg-[#6E2E2E] text-white"
            data-testid="pr-ovp-apply"
          >
            Apply all to this draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverpaymentItemCard({ it, busy, onAct }) {
  const sessions = it.sessions || [];
  const creditCents = (it.lc_ns_credit_units || 0) * (it.lc_ns_rate_cents || 0);
  return (
    <div className="rounded-lg border" style={{ borderColor: C.border }} data-testid={`pr-ovp-item-${it.adj_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 px-3 py-2" style={{ background: '#FBF6F6' }}>
        <div className="text-[12px] space-y-0.5">
          <div className="font-semibold" style={{ color: C.ink }}>Paid in {it.source_run_period_start} → {it.source_run_period_end}</div>
          <div style={{ color: C.soft }} data-testid={`pr-ovp-math-${it.adj_id}`}>
            Paid {it.shows_paid} Shows · SimplePractice now {it.shows_now} · <b>{it.units_to_deduct} lost</b> × {usd(it.therapy_rate_cents)}
            {it.lc_ns_credit_units > 0 && <> − {it.lc_ns_credit_units} now LC/NS × {usd(it.lc_ns_rate_cents)} ({usd(creditCents)} still owed)</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="tabular-nums font-bold text-[13px]" style={{ color: '#8A3A3A' }}>−{usd(it.cents)}</div>
            {(it.units_to_deduct || 0) > 0 && (
              <div className="tabular-nums text-[10.5px]" style={{ color: '#8A3A3A' }} data-testid={`pr-ovp-units-${it.adj_id}`}>
                −{it.units_to_deduct} unit{it.units_to_deduct === 1 ? '' : 's'}
              </div>
            )}
          </div>
          <button onClick={() => onAct(it.adj_id, 'Hold')} disabled={busy} className="px-2 py-1 text-[11px] rounded border hover:bg-[#FBF5E8]" style={{ borderColor: '#B08A3A', color: '#7A5F1F' }} data-testid={`pr-ovp-hold-${it.adj_id}`}>
            <Pause size={10} className="inline mr-0.5" /> Hold
          </button>
          <button onClick={() => onAct(it.adj_id, 'Waived')} disabled={busy} className="px-2 py-1 text-[11px] rounded border hover:bg-[#F1EEF4]" style={{ borderColor: C.soft, color: C.soft }} data-testid={`pr-ovp-waive-${it.adj_id}`}>
            <X size={10} className="inline mr-0.5" /> Waive
          </button>
        </div>
      </div>
      {sessions.length === 0 ? (
        <div className="px-3 py-2 text-[11px]" style={{ color: C.soft }}>
          The Show count dropped, but no changed appointment was found for this therapist. Check the SP Changes tab before applying.
        </div>
      ) : (
        <table className="w-full text-[12px]">
          <thead style={{ color: C.soft }}>
            <tr><th className="text-left px-3 py-1">Client</th><th className="text-left px-3 py-1">DOS</th><th className="text-left px-3 py-1">At payroll → Now in SP</th><th className="text-left px-3 py-1">Detected</th></tr>
          </thead>
          <tbody>
            {sessions.map(se => (
              <tr key={se.row_id} className="border-t" style={{ borderColor: C.border }} data-testid={`pr-ovp-session-${se.row_id}`}>
                <td className="px-3 py-1.5 font-semibold" style={{ color: C.ink }}>{se.client}</td>
                <td className="px-3 py-1.5 tabular-nums">{se.dos}</td>
                <td className="px-3 py-1.5 text-[11px]">
                  <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#EFE9F5', color: C.plum }}>{se.status_at_payroll}</span>
                  <span className="mx-1">→</span>
                  <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#FBECEC', color: '#8A3A3A' }}>{se.current_status}</span>
                </td>
                <td className="px-3 py-1.5 text-[11px]" style={{ color: C.soft }}>{(se.changed_at || '').slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export { CurrentRun, DetailTable, UnlockDialog, ValidationBlock, KpiRow };
